import React, { useLayoutEffect, useRef } from 'react';
import JobLogViewer from './JobLogViewer';

// <dialog-box> is a plain custom element (js/components/DialogBox.js, registered
// globally from js/entry.js), so TSX needs to be told it exists. Same pattern as
// SyncSettings.tsx and PlayerColumn.tsx.
//
// `show` is deliberately absent: it's a method on DialogBox.prototype, and React
// assigns to a custom element as a *property* when the name exists on the
// instance - passing show="true" would overwrite the method that
// connectedCallback and attributeChangedCallback both call. We call show() from
// the ref effect instead.
declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'dialog-box': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          'dialog-title'?: string;
          width?: string;
          height?: string;
          noscroll?: string;
        },
        HTMLElement
      >;
    }
  }
}

/** The parts of DialogBox we drive imperatively. */
type DialogBoxElement = HTMLElement & {
  show(): void;
  close(): void;
  autoKill: boolean;
  onCloseHandler: (() => void) | null;
};

interface JobLogDialogProps {
  serverId: number;
  queueName: string;
  jobId: number;
  /** Shown in the title bar. */
  title: string;
  onClose: () => void;
}

/** A job's logs in a modal. Closing is always driven by the element itself. */
export default function JobLogDialog({
  serverId,
  queueName,
  jobId,
  title,
  onClose,
}: JobLogDialogProps) {
  const dialogRef = useRef<HTMLElement | null>(null);

  // Kept in a ref so the effect below can stay mount-only without going stale.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // useLayoutEffect, not useEffect: autoKill and onCloseHandler have to be in
  // place before anything can fire, and show() before paint so there's no flash.
  useLayoutEffect(() => {
    const dialog = dialogRef.current as DialogBoxElement | null;

    if (!dialog) return;

    // DialogBox removes its own host element on close. React owns this node, so
    // letting the element delete itself leaves React removing a node that has
    // already gone.
    dialog.autoKill = false;

    // A property with a setter, not an attribute, so it can't come from JSX.
    // Both close paths - the title-bar X and Escape - funnel through here.
    dialog.onCloseHandler = () => onCloseRef.current();

    dialog.show();

    return () => {
      // DialogBox adds its Escape listener to `window` and never removes it, so
      // a discarded element can still be closed. Cut the link back to React
      // before we let go of it.
      dialog.onCloseHandler = null;
    };
  }, []);

  return (
    <dialog-box
      ref={(element) => {
        dialogRef.current = element;
      }}
      dialog-title={`Logs — ${title}`}
      width="900"
      height="600"
      // Read once in connectedCallback, so it has to be an attribute set before
      // insertion - a ref effect would run too late. Stops .dialog-content from
      // scrolling as well, leaving the textarea as the only scroller.
      noscroll="noscroll"
    >
      {/* No button bar: the title bar's close icon and Escape both close the
          dialog, and a Close button would only repeat them. */}
      <div slot="content" className="job-log-dialog__content">
        <JobLogViewer serverId={serverId} queueName={queueName} jobId={jobId} />
      </div>
    </dialog-box>
  );
}
